/**
 * @fileoverview Header.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react'
import DynamicImage from '../ui/DynamicImage';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

function Header({
  bgLow = "/hero-bg-low.webp",
  bgHigh = "/hero-bg.webp",
  subtitle,
  title,
  description,
  buttons
}) {
  const { t } = useTranslation();

  const finalSubtitle = subtitle || t('header.subtitle');
  const finalTitle = title || (
    <>
      {t('header.title_line1')} <br />
      {t('header.title_line2')}
    </>
  );
  const finalDescription = description || t('header.desc');
  const finalButtons = buttons || (
    <>
      <Link to="/tour-plans" className='px-8 py-3.5 bg-KITH-primary text-sm text-white font-bold rounded-full hover:bg-KITH-primary-dark transition-all transform hover:scale-105 shadow-xl'>
          {t('header.btn_explore')}
      </Link>
      <Link to="/about-us" className='px-8 py-3.5 bg-white/10 backdrop-blur-md border border-white/20 text-sm text-white font-bold rounded-full hover:bg-white/20 transition-all transform hover:scale-105'>
          {t('header.btn_story')}
      </Link>
    </>
  );

  return (
    <div className='relative w-full h-screen flex items-center justify-center overflow-hidden'>
      <Helmet>
        <link rel="preload" as="image" href={bgHigh} fetchpriority="high" />
      </Helmet>
      
      {/* Background with Overlay */}
      <div className='absolute inset-0 z-0'>
        <DynamicImage
          LowImage={bgLow}
          HighImage={bgHigh}
          loadingAttr="eager"
          fetchPriority="high"
        />
        <div className='absolute inset-0 bg-black/40 backdrop-blur-[2px]'></div>
      </div>

      {/* Content */}
      <div className='relative z-10 px-[5%] md:px-[10%] text-center flex flex-col items-center gap-6'>
        <div className='flex flex-col gap-3 max-w-3xl'>

            <motion.span
              className='text-KITH-primary font-bold tracking-[0.2em] uppercase text-xs'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              {finalSubtitle}
            </motion.span>

            <motion.h1
              className='text-2xl md:text-4xl font-black text-white leading-[1.2]'
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
                {finalTitle}
            </motion.h1>

            <motion.p
              className='text-white/70 text-sm md:text-base font-medium max-w-xl mx-auto'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
                {finalDescription}
            </motion.p>
        </div>

        {finalButtons && (
          <motion.div
            className='flex flex-wrap gap-4 justify-center mt-4'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
          >
              {finalButtons}
          </motion.div>
        )}

      </div>
      
      {/* Scroll Indicator */}
      <motion.div
        className='absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60 z-10'
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        transition={{ duration: 1, delay: 1.2 }}
      >
          <span className='text-[10px] text-white font-bold uppercase tracking-widest'>{t('header.scroll_down')}</span>
          <div className='w-[1px] h-12 bg-gradient-to-b from-white to-transparent'></div>
      </motion.div>
    </div>
  );
}

export default Header